import { Alert } from '@mui/material';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import {
    FormInputGroup,
    FormSection,
    FormSelect,
    FormText,
} from 'components/form';
import { FormModal } from 'components/form/FormModal/FormModal';
import { reportIssueOptions } from 'lib/constants';
import { submitDeviceHelp } from 'lib/firebase';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { StoredUser } from 'store/features/authentication';
import { activateSnackbar } from 'store/features/ui';
import { useLazyPatchUpdateDeviceQuery } from 'store/services';
import { Device } from 'types';
import * as yup from 'yup';
import { OfflineLedSequences } from './OfflineLedSequences/OfflineLedSequences';

interface ReportsIssueModalProps extends DefaultOpenModalProps {
    device: Device;
    user: StoredUser | null;
}

interface ReportIssueFormInput {
    type: string;
    description: string;
    selectedLedSequence: string;
}

const issueSchema = yup.object({
    type: yup.string().required(),
    description: yup.string().required(),
    selectedLedSequence: yup.string().when('type', {
        is: (issueType: string) => issueType === 'offline',
        then: (schema) => schema.required(),
    }),
});

const defaultValues: ReportIssueFormInput = {
    type: '',
    description: '',
    selectedLedSequence: '',
};

export const ReportIssueModal = ({
    device,
    user,
    modalOpen,
    setModalOpen,
}: ReportsIssueModalProps) => {
    const dispatch = useDispatch();

    const [updateDeviceQuery] = useLazyPatchUpdateDeviceQuery();

    const onSubmit = (data: FieldValues) => {
        const { type, description, selectedLedSequence } = data;
        if (!type || !description || !user) {
            return;
        }

        const selectedIssue = reportIssueOptions[type];

        // Include LED sequence info, if available. Append it on the to the description (which is a field specifically processed by backend stuff that creates a zendesk ticket)
        const descriptionWithTroubleshooting = selectedLedSequence
            ? `${description}\
        
        
            ** User selected LED troubleshooting sequence name: ${
                selectedLedSequence ?? 'none provided'
            }`
            : description;

        submitDeviceHelp(
            user,
            device,
            selectedIssue,
            descriptionWithTroubleshooting,
            selectedLedSequence
        ).then((result) => {
            if (result) {
                if (selectedIssue.action === 'retire') {
                    updateDeviceQuery({
                        deviceId: device.deviceId,
                        updatedFields: { status: 'retired' }, // TODO: does this still work? I think we have to use a specific endpoint?
                    });
                }
                dispatch(
                    activateSnackbar({
                        message: selectedIssue.follow_up,
                        severity: 'success',
                    })
                );
            }
        });
    };

    const onError = (errors: FieldErrors) => {
        dispatch(
            activateSnackbar({
                message: `There was a problem submitting your ticket: ${errors}`,
                severity: 'error',
            })
        );
    };

    return (
        <FormModal
            validationSchema={issueSchema}
            defaultValues={defaultValues}
            title="Help"
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onValidHandler={onSubmit}
            onErrorHandler={onError}
            testId="ReportIssueModal"
        >
            <ReportIssueForm user={user} />
        </FormModal>
    );
};

interface ReportIssueFormProps {
    user: StoredUser | null;
}

const ReportIssueForm = ({ user }: ReportIssueFormProps) => {
    const { watch } = useFormContext();

    const typeOptions = Object.keys(reportIssueOptions).map((opt) => ({
        name: reportIssueOptions[opt].text,
        value: opt,
        disabled: opt === 'clearData' && user?.protected?.roleId !== 0,
    }));

    const selectedType = watch('type');

    const confirmationMessage = reportIssueOptions[selectedType]?.confirm_text;

    return (
        <div>
            <FormSection title="Details">
                <strong>
                    We are sorry that you are experiencing an issue with your
                    monitor. We are here to help!
                </strong>
                <FormInputGroup>
                    <FormSelect
                        field="type"
                        label="Issue Type"
                        options={typeOptions}
                    />
                </FormInputGroup>
                <FormInputGroup>
                    <FormText field="description" label="Description" />
                </FormInputGroup>
                <Alert severity="info">
                    Please be as detailed in your description as possible.
                </Alert>
            </FormSection>

            <FormSection title="Troubleshooting">
                {selectedType === 'offline' ? (
                    <OfflineLedSequences />
                ) : (
                    <>
                        This type of issue does not have troubleshooting steps.
                        Please proceed to the next tab.
                    </>
                )}
            </FormSection>

            <FormSection title="Next Steps">
                <Alert severity="info">
                    {confirmationMessage ?? (
                        <>
                            The issue you have selected:{' '}
                            {reportIssueOptions[selectedType]?.text}
                        </>
                    )}
                </Alert>
                <strong>Do you wish to proceed?</strong>
            </FormSection>
        </div>
    );
};
